/**
 * Звонок клиента в ленте «Чатов»: переписка и звонки одной хронологией.
 *
 * Просьба руководителя КЦ от 10.10.26 — открыл диалог клиента и видишь всё, что с ним было:
 * сообщения любых каналов и звонки, которые можно прослушать и прочитать. Данные — своя копия
 * (`chat/calls.py`), запись отдаёт `GET /api/calls/{id}/record`.
 * Спека `docs/specs/2026-10-14-chat-calls-design.md`.
 */
import { ChevronDown, PhoneIncoming, PhoneMissed, PhoneOutgoing } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import type { ChatCall } from "@/lib/clientChats";

const hhmm = (iso: string) => iso.slice(11, 16);
const ddmm = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`;
/** 95 с — «1:35», 40 с — «40 с». */
const dur = (sec: number) => (sec >= 60 ? `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}` : `${sec} с`);
const TALK: Record<string, string> = { talk: "разговор", none: "разговора не было", unclear: "не разобрать" };

export function CallBubble({ c }: { c: ChatCall }) {
  const [open, setOpen] = useState(false);
  const missed = !c.answered;
  const Icon = missed ? PhoneMissed : c.out ? PhoneOutgoing : PhoneIncoming;
  const title = c.out ? "Исходящий" : "Входящий";
  return (
    <div
      className="my-1 flex max-w-[min(85%,520px)] flex-none flex-col gap-1 self-center rounded-[14px] bg-[var(--tg-surface)] px-3 py-2 text-[13px] shadow-[var(--tg-shadow)]"
      data-annot="звонок в ленте чата"
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
        <Icon size={15} className={missed ? "text-err" : "text-ok"} />
        <Link to={`/calls/${c.id}`} className="font-medium hover:underline" title="Карточка звонка">
          {title}
        </Link>
        <span className="tabular-nums text-[var(--tg-sec)]">
          {ddmm(c.date)} {hhmm(c.date)}
        </span>
        {missed ? (
          <span className="text-err">без ответа</span>
        ) : (
          <span className="tabular-nums text-[var(--tg-sec)]" title={c.talk ? TALK[c.talk] : "модель ещё не размечала"}>
            {dur(c.duration)}
            {c.talk === "none" && " · без разговора"}
          </span>
        )}
        {c.user && <span className="text-[var(--tg-sec)]">· {c.user}</span>}
      </div>
      {c.record && (
        <audio controls preload="none" src={`/api/calls/${c.id}/record`} className="h-8 w-full max-w-[320px]" />
      )}
      {c.transcript && (
        <>
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="flex items-center gap-1 self-start text-[12.5px] text-[var(--tg-sec)] hover:text-[var(--tg-text)]"
          >
            <ChevronDown size={14} className={open ? "rotate-180" : undefined} />
            {open ? "скрыть расшифровку" : "расшифровка"}
          </button>
          {open && <p className="whitespace-pre-wrap text-[12.5px] text-[var(--tg-text)]">{c.transcript}</p>}
        </>
      )}
    </div>
  );
}
